import React from "react";
import { Link } from "react-router-dom";
import SEO from "@components/SEO";
import { tours } from "@data/tours";
import { createBreadcrumbStructuredData } from "../utils/structuredData";
import {
  HomeIcon,
  TruckIcon,
  MapIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

const ToursPage: React.FC = () => {
  const getRegionTitle = (region: string) => {
    switch (region) {
      case "north-island":
        return "North Island";
      case "south-island":
        return "South Island";
      case "combo-island":
        return "Combo Island";
      default:
        return region;
    }
  };

  return (
    <div>
      <SEO
        title="New Zealand Tours Packages | North, South & Combo Island"
        description="New Zealand tours packages for travellers from Malaysia and Southeast Asia. Customisable North Island, South Island and combo itineraries with Muslim-friendly options."
        path="/tours"
        structuredData={createBreadcrumbStructuredData([
          { name: "Home", url: "/" },
          { name: "Tours", url: "/tours" },
        ])}
      />
      {/* Hero Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">
            Discover New Zealand
          </h1>
          <p className="text-xl text-primaryText max-w-3xl mx-auto">
            Choose from our carefully crafted tours showcasing the best of New
            Zealand's stunning landscapes, rich culture, and unforgettable
            experiences
          </p>
        </div>
      </section>

      {/* Tours Description */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-700 leading-relaxed">
            These are itineraries showcasing what we can offer. All our tours
            are highly customizable - we can adapt any itinerary to include your
            preferred locations and adjust it to fit your schedule, with
            guidance from our experienced team.
          </p>
        </div>
      </section>

      {/* Tours Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tours.map((tour) => (
              <div
                key={tour.id}
                className="flex flex-col h-full bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 group"
              >
                {/* Tour Image */}
                <Link to={`/tours/${tour.region}`} className="relative">
                  <img
                    src={`${import.meta.env.VITE_CLOUDFRONT_URL}${
                      tour.featuredImage
                    }`}
                    alt={tour.title}
                    className="w-full h-[250px] object-cover"
                  />
                  <div className="absolute top-4 left-4 bg-primary  text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {getRegionTitle(tour.region)}
                  </div>
                </Link>

                {/* Tour Content */}
                <div className="p-6 h-full flex flex-col justify-between">
                  <div className="flex flex-col mb-4">
                    <h3 className="text-xl font-bold text-primaryText mb-2 group-hover:text-primary transition-colors">
                      {tour.title}
                    </h3>
                    <p className="text-gray-600">{tour.description}</p>
                  </div>

                  <div className="flex flex-col space-y-2">
                    {/* Tour Details */}
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center text-primaryText">
                        <svg
                          className="w-4 h-4 mr-1 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        {tour.duration}+ days recommended
                      </div>
                      {/* <div className="flex items-center text-primaryText">
                        <svg
                          className="w-4 h-4 mr-1 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        From ${tour.price}/ pax
                      </div> */}
                    </div>
                    {/* View Tour Button */}
                    <Link
                      to={`/tours/${tour.region}`}
                      className="block w-full bg-primary text-white text-center py-2 px-4 rounded-md font-semibold hover:bg-secondary hover:text-primary transition-all duration-300"
                    >
                      View Tour Details
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tour Features */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primaryText mb-4">
              What's Included in Our Tours
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              All our tours are designed to provide you with the best New
              Zealand experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <HomeIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Quality Accommodation
              </h3>
              <p className="text-gray-600 text-sm">
                Comfortable accommodation in prime locations
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <TruckIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Transportation
              </h3>
              <p className="text-gray-600 text-sm">
                Modern vehicles with full insurance coverage
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <UserIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Local based driver
              </h3>
              <p className="text-gray-600 text-sm">
                The option to have a local based driver to assist you with your
                tour
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MapIcon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-primaryText mb-2">
                Tour Itinerary
              </h3>
              <p className="text-gray-600 text-sm">
                Detailed itinerary with all the information you need
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-primaryText mb-4">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-xl text-primaryText mb-8">
            We can create a custom itinerary tailored to your specific interests
            and schedule
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-block bg-primary text-white px-8 py-3 rounded-md font-semibold hover:bg-opacity-90 transition-all duration-300"
            >
              Custom Tour Inquiry
            </a>
            <a
              href="/activities"
              className="inline- border-2 border-primary bg-white text-primary px-8 py-3 rounded-md font-semibold hover:bg-gray-100 transition-all duration-300"
            >
              Browse Activities
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ToursPage;
